import { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Project } from '../../../mocks/projects';
import { allProjects } from '../../../mocks/projects';
import { useScrollReveal, revealClass } from '../../../hooks/useScrollReveal';

interface ProjectContentProps {
  project: Project;
}

export default function ProjectContent({ project }: ProjectContentProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Drag-to-pan state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  const galleryImages = project.additionalImages ? project.additionalImages.slice(1) : [];

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setZoomLevel(1);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    setZoomLevel(1);
  }, []);

  const goNext = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % galleryImages.length);
    setZoomLevel(1);
  }, [lightboxIndex, galleryImages.length]);

  const goPrev = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + galleryImages.length) % galleryImages.length);
    setZoomLevel(1);
  }, [lightboxIndex, galleryImages.length]);

  const zoomIn = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevel((z) => Math.min(z + 0.5, 4));
  }, []);

  const zoomOut = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevel((z) => Math.max(z - 0.5, 0.5));
  }, []);

  const resetZoom = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevel(1);
  }, []);

  // Mouse drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: el.scrollLeft,
      scrollTop: el.scrollTop,
    };
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    el.scrollLeft = dragStart.current.scrollLeft - dx;
    el.scrollTop = dragStart.current.scrollTop - dy;
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    const el = scrollContainerRef.current;
    if (el) {
      el.style.cursor = zoomLevel > 1 ? 'grab' : 'default';
      el.style.userSelect = '';
    }
  }, [zoomLevel]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      const el = scrollContainerRef.current;
      if (el) {
        el.style.cursor = zoomLevel > 1 ? 'grab' : 'default';
        el.style.userSelect = '';
      }
    }
  }, [zoomLevel]);

  // Related projects: same subSection, exclude current
  const related = allProjects
    .filter((p) => p.subSection === project.subSection && p.id !== project.id)
    .slice(0, 3);

  const { ref: overviewRef, isVisible: overviewVisible } = useScrollReveal<HTMLDivElement>();
  const { ref: galleryRef, isVisible: galleryVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.05 });
  const { ref: relatedRef, isVisible: relatedVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.05 });

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20">
      {/* Lightbox overlay */}
      {lightboxIndex !== null && galleryImages.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Top controls */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 z-10">
            <span className="text-white/60 text-sm font-sans">
              {lightboxIndex + 1} / {galleryImages.length}
            </span>

            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={zoomOut}
                disabled={zoomLevel <= 0.5}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                title="Zoom out"
              >
                <i className="ri-zoom-out-line text-lg" />
              </button>
              <button
                onClick={resetZoom}
                className="px-3 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-sans transition-colors cursor-pointer whitespace-nowrap"
                title="Reset zoom"
              >
                {Math.round(zoomLevel * 100)}%
              </button>
              <button
                onClick={zoomIn}
                disabled={zoomLevel >= 4}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                title="Zoom in"
              >
                <i className="ri-zoom-in-line text-lg" />
              </button>
            </div>

            <button
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              onClick={closeLightbox}
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Prev arrow */}
          {galleryImages.length > 1 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
          )}

          {/* Scrollable + draggable image container */}
          <div
            ref={scrollContainerRef}
            className="w-full h-full overflow-auto p-16"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.3) transparent',
              cursor: zoomLevel > 1 ? 'grab' : 'default',
            }}
            onClick={(e) => {
              if (!isDragging.current) closeLightbox();
              e.stopPropagation();
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            <div
              className="min-w-full min-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: 'center center',
                  transition: isDragging.current ? 'none' : 'transform 0.2s ease',
                  flexShrink: 0,
                }}
              >
                <img
                  src={galleryImages[lightboxIndex]}
                  alt={`${project.title} - image ${lightboxIndex + 2}`}
                  className="max-w-[80vw] max-h-[80vh] object-contain rounded-lg select-none block"
                  draggable={false}
                />
              </div>
            </div>
          </div>

          {/* Next arrow */}
          {galleryImages.length > 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
            >
              <i className="ri-arrow-right-s-line text-2xl" />
            </button>
          )}

          {/* Thumbnail strip */}
          {galleryImages.length > 1 && (
            <div
              className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-2 px-6 py-4 overflow-x-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {galleryImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => { setLightboxIndex(i); setZoomLevel(1); }}
                  className={`flex-shrink-0 w-14 h-9 rounded overflow-hidden border-2 transition-all cursor-pointer ${
                    i === lightboxIndex ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">

        {/* Main content column */}
        <div className="lg:col-span-2 space-y-14">

          {/* Overview */}
          <div ref={overviewRef} className={revealClass(overviewVisible)}>
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-6">Overview</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed whitespace-pre-line">
              {project.description}
            </p>
          </div>

          {/* Full content */}
          {project.fullContent && (
            <div className={revealClass(overviewVisible, 100)}>
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-6">About This Project</h2>
              <div
                className="font-sans text-base text-gray-700 leading-relaxed prose prose-gray max-w-none"
                dangerouslySetInnerHTML={{ __html: project.fullContent }}
              />
            </div>
          )}

          {/* Gallery — moved up, right after overview/about */}
          <div ref={galleryRef} className={revealClass(galleryVisible)}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl font-bold text-gray-900">Gallery</h2>
              {galleryImages.length > 0 && (
                <span className="font-sans text-xs text-gray-400 uppercase tracking-wider">
                  {galleryImages.length} image{galleryImages.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {galleryImages.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {galleryImages.map((img, i) => (
                  <div
                    key={i}
                    className={`w-full overflow-hidden rounded-lg cursor-pointer group relative bg-stone-100 aspect-[4/3] ${revealClass(galleryVisible, Math.min(i * 80, 400))}`}
                    onClick={() => openLightbox(i)}
                  >
                    <img
                      src={img}
                      alt={`${project.title} - image ${i + 2}`}
                      className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-[1.02]"
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 rounded-lg flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center gap-2">
                        <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full backdrop-blur-sm">
                          <i className="ri-zoom-in-line text-xl text-white drop-shadow-lg" />
                        </div>
                        <span className="font-sans text-xs text-white/80 bg-black/30 px-2 py-0.5 rounded-full">
                          {i + 1} / {galleryImages.length}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-gray-200 p-10 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-image-2-line text-3xl text-gray-300" />
                </div>
                <p className="font-sans text-sm text-gray-400">
                  Additional images coming soon
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-8">

          {/* Exhibition / Film Details — moved to top of sidebar */}
          {(project.year || project.venue) && (
            <div className={`bg-stone-50 rounded-lg p-6 border-l-4 border-amber-400 ${revealClass(overviewVisible, 150)}`}>
              <h3 className="font-serif text-base font-bold text-gray-900 mb-5">
                {project.subSection === 'film' ? 'Film Details' : 'Exhibition Details'}
              </h3>
              <div className="space-y-4">
                {project.year && (
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-calendar-line text-amber-600 text-base" />
                    </div>
                    <div>
                      <p className="font-sans text-xs uppercase tracking-wider text-gray-400 mb-0.5">
                        {project.subSection === 'film' ? 'Release Year' : 'Exhibition Period'}
                      </p>
                      <p className="font-sans text-sm font-semibold text-gray-900">{project.year}</p>
                    </div>
                  </div>
                )}
                {project.venue && project.subSection !== 'film' && (
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-map-pin-line text-amber-600 text-base" />
                    </div>
                    <div>
                      <p className="font-sans text-xs uppercase tracking-wider text-gray-400 mb-0.5">Platform</p>
                      <p className="font-sans text-sm font-semibold text-gray-900">{project.venue}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-user-line text-amber-600 text-base" />
                  </div>
                  <div>
                    <p className="font-sans text-xs uppercase tracking-wider text-gray-400 mb-0.5">
                      {project.subSection === 'film' ? 'Researcher' : 'Curator'}
                    </p>
                    <p className="font-sans text-sm font-semibold text-gray-900">Oludamola Adebowale</p>
                  </div>
                </div>
                {project.subSection !== 'film' && (
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-price-tag-3-line text-amber-600 text-base" />
                    </div>
                    <div>
                      <p className="font-sans text-xs uppercase tracking-wider text-gray-400 mb-0.5">Category</p>
                      <p className="font-sans text-sm font-semibold text-gray-900">{project.category}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Enquiries — below Exhibition Details */}
          <div className={`bg-amber-50 rounded-lg p-6 border border-amber-100 ${revealClass(overviewVisible, 200)}`}>
            <h3 className="font-serif text-base font-bold text-gray-900 mb-3">Enquiries</h3>
            <p className="font-sans text-xs text-gray-600 leading-relaxed mb-4">
              For collaboration, loans, or academic enquiries related to this project, please get in touch.
            </p>
            <Link
              to="/press#contact"
              className="inline-flex items-center gap-2 font-sans text-xs font-medium bg-gray-900 text-white px-4 py-2.5 rounded-md hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line text-xs" />
              </div>
              Send Enquiry
            </Link>
          </div>

          {/* Back link */}
          <a
            href={`/#${project.section}`}
            className={`flex items-center gap-2 font-sans text-sm text-gray-500 hover:text-gray-900 transition-colors cursor-pointer ${revealClass(overviewVisible, 250)}`}
          >
            <i className="ri-arrow-left-line" />
            Back to {project.section === 'research' ? 'Research Projects' : project.section === 'portfolio' ? 'Portfolio' : 'Archives'}
          </a>
        </aside>
      </div>

      {/* Related Projects */}
      {related.length > 0 && (
        <div ref={relatedRef} className={`mt-24 pt-16 border-t border-stone-200 ${revealClass(relatedVisible)}`}>
          <h2 className="font-serif text-2xl font-bold text-gray-900 mb-10">Related Projects</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {related.map((rel, i) => (
              <Link
                key={rel.id}
                to={`/project/${rel.slug}`}
                className={`group block bg-stone-50 rounded-lg overflow-hidden hover:bg-stone-100 transition-colors duration-300 cursor-pointer ${revealClass(relatedVisible, i * 150)}`}
              >
                <div className="w-full aspect-square overflow-hidden">
                  <img
                    src={rel.image}
                    alt={rel.title}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  {rel.subSection !== 'film' && (
                    <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold mb-2">{rel.category}</p>
                  )}
                  <h3 className="font-serif text-base font-semibold text-gray-900 leading-snug line-clamp-2 mb-2">
                    {rel.title}
                  </h3>
                  {rel.year && <p className="font-sans text-xs text-gray-400">{rel.year}</p>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
